import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { X, Menu } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../Helper/api";
import { LANGUAGES, formatDateTime } from "../i18n";
import PasswordOperationsPage from "../pages/PasswordOperationsPage";
import * as authAction from "../Store/_redux/AuthStore/authActions";

// SVG Simgeler
import homeIcon from "../assets/home.svg";
import usersIcon from "../assets/users.svg";
import addUserIcon from "../assets/add-user.svg";
import exitIcon from "../assets/exit.svg";
import infoIcon from "../assets/info.svg";
import lockIcon from "../assets/lock.svg";
import pavzaLogo from "../assets/PavzaLogo.svg";

// Menü sayfaları: adminOnly olanlar yalnızca yöneticiye gösterilir
const getMenuItems = (isAdmin) => [
  { to: "/dashboard", icon: homeIcon, labelKey: "sidebar.dashboard" },
  { to: "/izinler", icon: infoIcon, labelKey: "sidebar.leaves" },
  { to: "/takvim", icon: infoIcon, labelKey: "sidebar.calendar" },
  { to: "/masraflar", icon: infoIcon, labelKey: isAdmin ? "sidebar.expenseManagement" : "sidebar.expenses" },
  { to: "/harcirahlar", icon: infoIcon, labelKey: "sidebar.travelAllowances" },
  { to: "/user", icon: usersIcon, labelKey: "sidebar.users", adminOnly: true },
  { to: "/signup", icon: addUserIcon, labelKey: "sidebar.signup", adminOnly: true },
  { to: "/aylik-rapor", icon: usersIcon, labelKey: "sidebar.report", adminOnly: true },
  { to: "/ayarlar", icon: lockIcon, labelKey: "sidebar.settings", adminOnly: true },
];

const sectionTitleClass = "text-[10px] text-gray-400 font-bold uppercase tracking-wider px-3 py-1.5 block";

function Navbar() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const auth = useSelector((state) => state.auth.loginUser);
  const token = useSelector((state) => state.auth.token);
  const isAdmin = auth?.userType === "admin";

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const notificationsRef = useRef(null);
  const langDropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      console.error("Bildirimler alınamadı:", err);
    }
  }, []);

  useEffect(() => {
    if (token) fetchNotifications();
  }, [token, fetchNotifications]);

  // Dışarı tıklayınca açık dropdown'ları kapat
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setNotificationsOpen(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleBellClick = async () => {
    const opening = !notificationsOpen;
    setNotificationsOpen(opening);
    if (!opening || unreadCount === 0) return;

    try {
      await api.patch("/notifications/read-all", {});
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      console.error("Okundu işareti hatası:", err);
    }
  };

  if (!token) return null;

  const closeMenu = () => setMenuOpen(false);

  const linkClass = (href) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all duration-200 ${
      location.pathname === href
        ? "bg-blue-600 text-white font-medium shadow-md shadow-blue-900/30"
        : "text-gray-300 hover:bg-white/10 hover:text-white"
    }`;

  const initials = auth ? `${auth.name?.[0] || ""}${auth.surname?.[0] || ""}`.toUpperCase() : "?";
  const currentLanguage = LANGUAGES.find((lang) => lang.code === i18n.resolvedLanguage) || LANGUAGES[0];
  const menuItems = getMenuItems(isAdmin).filter((item) => !item.adminOnly || isAdmin);

  return (
    <div className="h-16 flex items-center justify-between px-6 bg-[#1a2340] border-b border-white/10 shadow-lg fixed top-0 left-0 w-full z-40 print:hidden">
      {/* Sol Kısım: Menü Butonu ve Logo */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => setMenuOpen(true)}
          className="flex items-center justify-center p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all duration-200 shadow-inner"
          title={t("navbar.menu")}
        >
          <Menu size={20} />
        </button>

        <div className="w-px h-6 bg-white/20"></div>

        <Link to="/dashboard" className="flex items-center">
          <img src={pavzaLogo} alt="Pavza Logo" className="h-6 brightness-0 invert cursor-pointer" />
        </Link>
      </div>

      {/* Sağ Kısım: Dil, Bildirimler ve Profil */}
      <div className="flex items-center gap-4">
        {/* Dil Seçici */}
        <div className="relative" ref={langDropdownRef}>
          <button
            onClick={() => setLangDropdownOpen((prev) => !prev)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium text-gray-200 transition duration-200 focus:outline-none"
          >
            <span className="text-base">{currentLanguage.flag}</span>
            <span className="hidden sm:inline">{currentLanguage.name}</span>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${langDropdownOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {langDropdownOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-100 rounded-xl shadow-lg z-50 py-1.5 overflow-hidden animate-fadeIn">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    i18n.changeLanguage(lang.code);
                    setLangDropdownOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition duration-150 ${
                    lang.code === currentLanguage.code
                      ? "bg-blue-50 text-blue-600 font-semibold"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <span className="text-base">{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bildirim zili */}
        <div className="relative" ref={notificationsRef}>
          <button onClick={handleBellClick} className="relative text-gray-300 hover:text-white transition-colors duration-150 mt-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {notificationsOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 text-gray-800">
              <div className="px-4 py-3 border-b font-semibold text-gray-700">{t("navbar.notifications")}</div>
              <ul className="max-h-72 overflow-y-auto">
                {notifications.length === 0 ? (
                  <li className="px-4 py-6 text-center text-gray-400 text-sm">{t("navbar.noNotifications")}</li>
                ) : (
                  notifications.map((n) => (
                    <li
                      key={n._id}
                      className={`px-4 py-3 text-sm border-b last:border-0 transition-colors duration-150 ${
                        !n.isRead ? "bg-blue-50/70 hover:bg-blue-50" : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <p className="text-gray-700">{n.message}</p>
                      <p className="text-gray-400 text-xs mt-1">{formatDateTime(n.createdAt)}</p>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Hoşgeldin yazısı */}
        <span className="text-sm text-gray-300 hidden sm:inline">
          {t("dashboard.welcome")}, <span className="font-semibold text-white">{auth?.name}</span>
        </span>

        {/* Avatar */}
        <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shadow-md border border-white/10">
          {initials}
        </div>
      </div>

      {/* Yan Açılır Menü (Drawer) — arka plan karartması */}
      <div
        onClick={closeMenu}
        className={`fixed inset-0 bg-black/60 backdrop-blur-xs z-40 transition-opacity duration-300 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      ></div>

      <div
        className={`fixed top-0 left-0 h-full w-72 bg-[#1a2340] text-white z-50 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out transform ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
          <Link to="/dashboard" onClick={closeMenu}>
            <img src={pavzaLogo} alt="Pavza Logo" className="h-6 brightness-0 invert" />
          </Link>
          <button onClick={closeMenu} className="text-gray-400 hover:text-white transition-colors p-1">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 flex flex-col gap-1">
          <span className={sectionTitleClass}>{t("navbar.pages")}</span>

          {menuItems.map((item) => (
            <Link key={item.to} to={item.to} className={linkClass(item.to)} onClick={closeMenu}>
              <img src={item.icon} alt="" className="w-4 h-4 brightness-200" />
              {t(item.labelKey)}
            </Link>
          ))}

          {isAdmin && (
            <>
              <span className={`${sectionTitleClass} mt-4 border-t border-white/10 pt-4`}>{t("navbar.actions")}</span>
              <button
                onClick={() => {
                  setIsPasswordModalOpen(true);
                  closeMenu();
                }}
                className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-all duration-200 w-full text-left"
              >
                <img src={lockIcon} alt="" className="w-4 h-4 brightness-200" />
                {t("sidebar.passwords")}
              </button>
            </>
          )}

          <div className="mt-auto pt-4 border-t border-white/10">
            <button
              className="flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors duration-200 w-full text-left"
              onClick={() => {
                dispatch(authAction.userLogout());
                closeMenu();
              }}
            >
              <img src={exitIcon} alt="" className="w-4 h-4 brightness-200" />
              {t("sidebar.logout")}
            </button>
          </div>
        </nav>
      </div>

      {/* Şifre İşlemleri Modalı */}
      {isPasswordModalOpen && (
        <div
          onClick={() => setIsPasswordModalOpen(false)}
          className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start justify-center p-4 pt-10"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-2xl w-full p-6 relative border shadow-2xl text-black"
          >
            <button
              onClick={() => setIsPasswordModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
              title={t("infoModal.close")}
            >
              <X size={24} />
            </button>
            <PasswordOperationsPage
              onCancel={() => setIsPasswordModalOpen(false)}
              onSuccess={() => setIsPasswordModalOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Navbar;
